import React, { useCallback, Fragment, useState, useContext, useEffect } from 'react';
import { graphql } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { TreeItem } from '@confluence/tree/entry-points/tree-types.ts';
import type { EditorPropertyType } from '@confluence/content-utils/entry-points/EditorPropertyType.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	BLOG_TREE_EXPERIENCE,
	BLOG_TREE_PAGINATION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useBlogTreeStore } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore.ts';

import { BlogTreeComponent } from './BlogTreeComponent.tsx';
import { flattenTreeData, getBlogpostNodes, isBlogTreeTruncated } from './dataTransformers.js';
import { PAGE_SIZE, BLOG_STATUSES } from './treeParameters.js';
import { BlogTreeErrorComponent } from './BlogTreeErrorComponent.tsx';
import { BLOG_TREE_METRIC } from './perf.config.ts';
import type {
	BlogTreeRootComponentQuery$data,
	BlogTreeRootComponentQuery$variables,
} from './__generated__/BlogTreeRootComponentQuery.graphql.ts';

const BlogTreeRootComponentQuery = graphql`
	query BlogTreeRootComponentQuery(
		$spaceKey: String!
		$first: Int
		$endCursor: String
		$statuses: [String]
		$orderBy: String
	) {
		content(
			spaceKey: $spaceKey
			type: "blogpost"
			first: $first
			orderby: $orderBy
			after: $endCursor
			status: $statuses
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				id
				title
				status
				blank
				properties(keys: ["emoji-title-published", "emoji-title-draft", "editor"]) {
					nodes {
						id
						key
						value
					}
				}
				links {
					webui
				}
				metadata {
					lastModifiedDate
					createdDate
				}
			}
		}
	}
`;

type BlogTreeRootComponentProps = {
	spaceKey: string;
	flags: FlagsStateContainer;
	pageTreeFinishedLoading: boolean;
};

const styles = cssMap({
	nav3EmptyStateTextWrapper: {
		paddingLeft: token('space.150'),
		color: token('color.text.subtlest'),
	},
	emptyStateTextWrapper: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		color: token('color.text.subtlest'),
	},
});

const i18n = defineMessages({
	noBlogsEmptyStateDescription: {
		id: 'blog-tree.empty-state-description',
		defaultMessage: 'No blogs in this space',
		description:
			'Empty state message displayed in the blog tree when there are no blog posts in the current space',
	},
	paginateErrorFlagDescription: {
		id: 'blog-tree.paginate.failed.description',
		defaultMessage:
			'We encountered a network error while loading more blogs. Refresh the page and try again. If this keeps happening, contact your space administrator.',
		description:
			'An error message that displays in a flag to let the user know that there was a problem while loading more content into their tree',
	},
	paginateErrorFlagTitle: {
		id: 'blog-tree.paginate.failed.title',
		defaultMessage: 'Something went wrong',
		description:
			'Error flag title displayed when loading more blog posts fails during pagination in the blog tree',
	},
});

export interface BlogTreeItem extends TreeItem {
	data: {
		title?: string;
		status?: string;
		blank?: boolean;
		emoji?: string;
		emojiFromProperties?: string;
		webui?: string;
		editorVersion?: EditorPropertyType;
		isSelected?: boolean;
		createdYear?: string;
	};
}

export const BlogTreeRootComponent = withFlags(
	({ flags, spaceKey, pageTreeFinishedLoading }: BlogTreeRootComponentProps) => {
		const experienceTracker = useContext(ExperienceTrackerContext);
		const intl = useIntl();
		const [fetchMoreError, setFetchMoreError] = useState<ApolloErrorInterop | undefined>(undefined);
		const [shouldForceRefetch, _] = useBlogTreeStore();

		const { error, loading, data, fetchMore, networkStatus, refetch } = useQueryInterop<
			BlogTreeRootComponentQuery$data,
			BlogTreeRootComponentQuery$variables
		>(BlogTreeRootComponentQuery, {
			errorPolicy: 'all',
			variables: {
				spaceKey,
				first: PAGE_SIZE,
				statuses: BLOG_STATUSES,
				orderBy: 'metadata.createdDate desc',
			},
			notifyOnNetworkStatusChange: true,
		});

		useEffect(() => {
			void refetch();
		}, [shouldForceRefetch, refetch]);

		const fetchMoreBlogs = useCallback(async () => {
			try {
				const endCursor = data?.content?.pageInfo?.endCursor || '';
				const { errors } = await fetchMore({
					variables: { endCursor },
					updateQuery: (previousQueryResult, { fetchMoreResult }) => {
						if (!fetchMoreResult) return previousQueryResult;

						return {
							content: {
								nodes: [
									...getBlogpostNodes(previousQueryResult),
									...getBlogpostNodes(fetchMoreResult),
								],
								__typename: 'PaginatedContentListWithChildren',
								pageInfo: fetchMoreResult?.content?.pageInfo || null,
							},
						};
					},
				});
				experienceTracker.succeed({
					name: BLOG_TREE_PAGINATION_EXPERIENCE,
				});
				if (errors) {
					// errors is of type GraphQLError[] but we need type ApolloErrorInterop / Error for ErrorDisplay.
					// throwing the error here converts it into an ApolloErrorInterop
					throw errors;
				}
			} catch (e) {
				// fetchMore errors are not surfaced in initial useQuery "error"
				setFetchMoreError(e);
				void flags.showErrorFlag({
					title: intl.formatMessage(i18n.paginateErrorFlagTitle),
					description: intl.formatMessage(i18n.paginateErrorFlagDescription),
					isAutoDismiss: true,
				});
			}
		}, [fetchMore, data, flags, intl, experienceTracker]);

		if (data && data?.content?.nodes?.length === 0) {
			return (
				<Box xcss={styles.emptyStateTextWrapper}>
					<FormattedMessage {...i18n.noBlogsEmptyStateDescription} />
				</Box>
			);
		}

		const blogTreeData = flattenTreeData(data ? data.content : null);

		return (
			<Fragment>
				<PageSegmentLoadStart metric={BLOG_TREE_METRIC} />
				<ExperienceStart name={BLOG_TREE_EXPERIENCE} />
				{error || fetchMoreError ? (
					<Fragment>
						{error && (
							<ErrorDisplay error={error}>
								<ExperienceFailure name={BLOG_TREE_EXPERIENCE} error={error} />
							</ErrorDisplay>
						)}
						{fetchMoreError && (
							<ErrorDisplay error={fetchMoreError}>
								<ExperienceFailure name={BLOG_TREE_PAGINATION_EXPERIENCE} error={fetchMoreError} />
							</ErrorDisplay>
						)}
						<BlogTreeErrorComponent />
					</Fragment>
				) : (
					<BlogTreeComponent
						spaceKey={spaceKey}
						blogTreeData={blogTreeData}
						shouldShowLoadMoreButton={data ? isBlogTreeTruncated(data.content) : false}
						fetchMoreBlogs={fetchMoreBlogs}
						networkStatus={networkStatus}
						loading={loading}
						pageTreeFinishedLoading={pageTreeFinishedLoading}
					/>
				)}
			</Fragment>
		);
	},
);
